import type { FC } from "react"
import type { GetServerSideProps } from "next/types"
import { Stack } from "@chakra-ui/react"

import ErrorBoundary from "common/components/ErrorBoundary"
import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import { ROUTES } from "common/constants/routes"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import { TALLY_DEFAULT_SOCIAL_PREVIEW } from "common/constants/logo"
import type { Organization } from "query/graphql"
import { isValidAccountId } from "web3/helpers/accountId"
import { fetchGovernanceById } from "governance/helpers/fetch"
import { fetchOrganizationBySlug } from "organization/helpers/fetcher"
import { BREADCRUMB } from "common/constants/breadcrumb"
import { isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import {
  getWhiteLabelRoute,
  getWhitelabelBreadcrumb,
} from "whitelabel/utils/breadcrumb"
import FaqsList from "claim/components/FAQs"
import HelpLinks from "claim/components/HelpLinks"
import { OrganizationProvider } from "organization/providers/OrganizationProvider"
import DaoLayoutBreadcrumb from "ui/layouts/dao/DaoLayoutBreadcrumb"

type Props = {
  organization: Organization
  isWhiteLabel: boolean
}
const FAQsPage: FC<Props> = ({ isWhiteLabel, organization }) => {
  const { slug, name } = organization

  const title = isWhiteLabel ? `${name} | FAQs` : `Tally | ${name} FAQs`
  const preview = {
    title,
    image: TALLY_DEFAULT_SOCIAL_PREVIEW,
    description: "Frequently asked questions.",
  }

  const breadcrumb = isWhiteLabel
    ? getWhitelabelBreadcrumb(
        BREADCRUMB.governance.faq(slug, name, isWhiteLabel),
      )
    : BREADCRUMB.governance.faq(slug, name)

  return (
    <ErrorBoundary section="FAQ">
      <OrganizationProvider organization={organization}>
        <MainLayout>
          <MainLayoutHead preview={preview} title={title} />
          <MainLayoutHeader
            isWhiteLabel={isWhiteLabel}
            organization={organization}
            redirectTo={{ logout: ROUTES.home() }}
          />
          <DaoLayoutBreadcrumb breadcrumb={breadcrumb} />
          <MainLayoutContainer>
            <Stack spacing={4}>
              <FaqsList
                organization={organization}
                title={`${organization.name} FAQs`}
              />

              <HelpLinks organization={organization} title="Need help?" />
            </Stack>
          </MainLayoutContainer>
          <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
        </MainLayout>
      </OrganizationProvider>
    </ErrorBoundary>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context
  const { governanceId: governanceIdParam } = query
  const slug = governanceIdParam as string

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance.organization) {
      return {
        redirect: {
          permanent: false,
          destination: isWhitelabel(context)
            ? getWhiteLabelRoute(
                ROUTES.governance.faq(governance.organization.slug),
              )
            : ROUTES.governance.faq(governance.organization.slug),
        },
      }
    }
  }

  const organization = await fetchOrganizationBySlug(slug, context)

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  return {
    props: {
      organization,
      isWhiteLabel: isWhitelabel(context),
    },
  }
}

export default FAQsPage
